// SoloPlayScreen — the live solo gameplay shell.
//
// Intentionally thin: the existing <Shop> component is already a
// polished, drag-and-drop-aware UI. SoloPlayScreen's job is to
// bootstrap the session, wire Shop's action callbacks to the live
// server (via soloStore), and swap to <BattleScreen> when /ready
// returns a replay.
//
// Screen state machine (delegated to soloStore):
//   idle      → mount: getMe → if 401, parent routes to PlayerCreationScreen
//   loading   → spinner placeholder (transient — gone within 1 RTT)
//   shop      → renders <Shop>
//   battle    → renders <BattleScreen battle meta onExit>
//   finished  → "run complete" cap; Begin Anew CTA
//   error     → toast row with the most recent error code

import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useSoloStore } from '../game/soloStore'
import { useAmbience } from '../sound'
import { Shop } from '../game/Shop'
import { ShopTutorial } from './ShopTutorial'
import {
  hasSeenShopTutorial,
  markShopTutorialSeen,
  consumeTutorialReplayRequest,
} from './shopTutorial'
import { type BattleMeta } from './BattleScreen'
import { PixiBattle } from '../pixi/PixiBattleLazy'
import { GLYPHS } from '../design-system'
import type { GlyphKey, PlayerProfile, SoloBattleResponse } from '@origami/shared'
import './SoloPlayScreen.css'

interface SoloPlayScreenProps {
  player: PlayerProfile
  /** Surfaces auth failure (401) so App can route to the creation
   *  form. Anything else stays in-screen as an error banner. */
  onSessionLost: () => void
}

export function SoloPlayScreen({ player, onSessionLost }: SoloPlayScreenProps): ReactElement {
  const session = useSoloStore((s) => s.session)
  const phase = useSoloStore((s) => s.phase)
  const pendingBattle = useSoloStore((s) => s.pendingBattle)
  const error = useSoloStore((s) => s.error)
  const unitsById = useSoloStore((s) => s.unitsById)
  const start = useSoloStore((s) => s.start)
  const loadCatalog = useSoloStore((s) => s.loadCatalog)
  const buy = useSoloStore((s) => s.buy)
  const buyToSlot = useSoloStore((s) => s.buyToSlot)
  const sell = useSoloStore((s) => s.sell)
  const reroll = useSoloStore((s) => s.reroll)
  const combine = useSoloStore((s) => s.combine)
  const reorder = useSoloStore((s) => s.reorder)
  const toggleLock = useSoloStore((s) => s.toggleLock)
  const ready = useSoloStore((s) => s.ready)
  const dismissBattle = useSoloStore((s) => s.dismissBattle)
  const notice = useSoloStore((s) => s.notice)
  const clearNotice = useSoloStore((s) => s.clearNotice)
  const buffFloaters = useSoloStore((s) => s.buffFloaters)
  const clearBuffFloater = useSoloStore((s) => s.clearBuffFloater)

  // First-time shop tutorial. Auto-opens once on the very first shop
  // (round 1) unless already seen; the "?" button and the Settings
  // replay path force it open regardless. `autoFiredRef` keeps the
  // auto-trigger to a single fire per mount.
  const [tutorialOpen, setTutorialOpen] = useState(false)
  const autoFiredRef = useRef(false)

  // Settings → Solo replay: a one-shot sessionStorage request consumed
  // on mount, opening the tour even though the seen flag is set.
  useEffect(() => {
    if (consumeTutorialReplayRequest()) setTutorialOpen(true)
  }, [])

  useEffect(() => {
    if (autoFiredRef.current) return
    if (phase === 'shop' && session?.shop.round === 1 && !hasSeenShopTutorial()) {
      autoFiredRef.current = true
      setTutorialOpen(true)
    }
  }, [phase, session])

  // Ambient bed follows the phase: shop loop while drafting, battle
  // loop during the replay, silence on the finished cap. Crossfades
  // on change; fades out when the screen unmounts.
  useAmbience(phase === 'battle' ? 'battle' : phase === 'shop' ? 'shop' : null)

  // Bootstrap on mount: load the unit catalog (used to resolve
  // glyphs for BattleScreen's replay rendering — BattleEvent payloads
  // carry unit_ids only) and start/resume the match.
  useEffect(() => {
    void loadCatalog()
    if (!session) void start()
  }, [session, start, loadCatalog])

  // Server-side cookie eviction → route to creation form.
  useEffect(() => {
    if (error === 'no_session') onSessionLost()
  }, [error, onSessionLost])

  if (!session) {
    return (
      <div className="paper sps-page sps-page--loading">
        <div className="sps-loading">unfolding the shop…</div>
      </div>
    )
  }

  // Battle phase: replay the round, then hand control back via dismissBattle.
  if (phase === 'battle' && pendingBattle) {
    const meta = buildBattleMeta(player, session.shop, pendingBattle.meta, unitsById)
    return (
      <PixiBattle
        battle={pendingBattle.replay}
        meta={meta}
        onExit={dismissBattle}
      />
    )
  }

  // Run complete: a small ceremonial cap.
  if (phase === 'finished') {
    return <RunComplete player={player} session={session} onSessionLost={onSessionLost} />
  }

  return (
    <div className="paper sps-page">
      <Shop
        state={session.shop}
        notice={notice}
        onClearNotice={clearNotice}
        buffFloaters={buffFloaters}
        onClearFloater={clearBuffFloater}
        actions={{
          onBuy: (slotIndex) => void buy(slotIndex),
          onBuyToSlot: (slotIndex, target, opts) =>
            void buyToSlot(
              slotIndex,
              { where: target.kind, position: target.index },
              opts,
            ),
          onSell: (where, slotIndex) => void sell(where, slotIndex),
          onReroll: () => void reroll(),
          onCombine: (from, to, opts) =>
            void combine(
              { where: from.kind, position: from.index },
              { where: to.kind, position: to.index },
              opts,
            ),
          onMove: (from, to) =>
            void reorder(
              { where: from.kind, position: from.index },
              { where: to.kind, position: to.index },
            ),
          onToggleSlotLock: (slotIndex) => void toggleLock(slotIndex),
          onOpenTutorial: () => setTutorialOpen(true),
          onReady: () => void ready(),
          onExit: () => {
            // Navigate back to the title without abandoning the run —
            // /api/solo/start stays idempotent so the player can
            // resume from the title's Continue Run.
            window.history.pushState(null, '', '?')
            window.dispatchEvent(new Event('url-changed'))
          },
        }}
      />
      {error && error !== 'no_session' && (
        <div className="sps-toast">
          <span className="sps-toast__dot" /> {humanizeError(error)}
        </div>
      )}
      <ShopTutorial
        open={tutorialOpen}
        onClose={() => {
          // Mark seen on every close (done / skip / manual). The flag
          // only suppresses the AUTO trigger; the "?" + Settings paths
          // ignore it, so this keeps the auto-open strictly one-shot.
          markShopTutorialSeen()
          setTutorialOpen(false)
        }}
      />
    </div>
  )
}

/** Map a server error code to a short status line. Falls through to
 *  the raw code so unfamiliar failures stay debuggable. */
function humanizeError(code: string): string {
  const map: Record<string, string> = {
    insufficient_gold: 'not enough gold',
    team_full: 'team is full',
    cannot_combine: 'those units cannot combine',
    cannot_reorder: 'cannot move to the same slot',
    slot_already_bought: 'already bought',
    invalid_slot_index: 'unknown slot',
    unit_not_found: 'no unit there',
    empty_team: 'place at least one unit before pressing ready',
    match_not_found: 'no active run — restart from the title',
    match_finished: 'this run is over',
  }
  return map[code] ?? code
}

/** 絵馬 (ema) — wooden votive plaque. The player's chosen mascot
 *  sits in the peaked roof as the household 家紋 (family crest);
 *  display_name + team_name occupy the body of the pentagon. Two
 *  vermillion cord strands attach at the shoulders through ink
 *  eyelet dots, suggesting the plaque is hung from a banner above.
 *  Sways gently around its top-center pivot.
 *
 *  Exported so the design gallery + future surfaces (battle-result
 *  screen, player profile, ledger views) can reuse the same plaque
 *  visual. The fixed-position outer wrapper (`.sps-ema`) is owned
 *  by the caller — `<EmaPlaque/>` itself is just the painted shape
 *  + content, and renders in normal flow. */
export function EmaPlaque({
  icon,
  displayName,
  teamName,
}: {
  icon: GlyphKey
  displayName: string
  teamName: string
}): ReactElement {
  const Glyph = GLYPHS[icon] ?? GLYPHS.crane!
  return (
    <div className="sps-ema__plaque">
      <svg
        className="sps-ema__shape"
        viewBox="0 0 120 190"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
      >
        <defs>
          <pattern
            id="sps-ema-grain"
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
          >
            <rect width="6" height="6" fill="#d6b282" />
            <line
              x1="0"
              y1="3"
              x2="6"
              y2="3"
              stroke="rgba(58, 42, 24, 0.08)"
              strokeWidth="1"
            />
            <line
              x1="0"
              y1="5"
              x2="6"
              y2="5"
              stroke="rgba(58, 42, 24, 0.04)"
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        {/* cord — two strands from the knot down to the plaque
           shoulders; suggests hanging from a single point above. */}
        <path
          d="M 60 5 L 5 65"
          stroke="var(--vermillion-deep)"
          strokeWidth="2.2"
          strokeLinecap="round"
          fill="none"
        />
        <path
          d="M 60 5 L 115 65"
          stroke="var(--vermillion-deep)"
          strokeWidth="2.2"
          strokeLinecap="round"
          fill="none"
        />
        <circle
          cx="60"
          cy="5"
          r="4"
          fill="var(--vermillion-deep)"
          stroke="var(--ink)"
          strokeWidth="1.4"
        />
        {/* pentagonal plaque — peak (60,33) → shoulders (5,65) /
           (115,65) → bottom corners (5,177) / (115,177). */}
        <polygon
          points="60,33 115,65 115,177 5,177 5,65"
          fill="url(#sps-ema-grain)"
          stroke="var(--ink)"
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {/* eyelet dots at the shoulders where the cord ties */}
        <circle cx="5" cy="65" r="2.2" fill="var(--ink)" />
        <circle cx="115" cy="65" r="2.2" fill="var(--ink)" />
      </svg>
      <div className="sps-ema__content">
        <div className="sps-ema__crest" aria-hidden>
          <Glyph size={36} />
        </div>
        <hr className="sps-ema__rule" aria-hidden />
        <div className="sps-ema__name">{displayName}</div>
        <div className="sps-ema__team">{teamName}</div>
      </div>
    </div>
  )
}

/** Build the BattleMeta shape BattleScreen expects from the player
 *  profile + the /ready response. The opponent is anonymous in solo
 *  (it's a ghost) so we synthesize a name.
 *
 *  unitGlyphs is built from the catalog: BattleEvent payloads
 *  carry unit_id only, and BattleScreen falls back to 'crane' when
 *  it can't find a glyph mapping. If the catalog hasn't loaded yet
 *  the map is empty and units render the crane fallback for a frame —
 *  acceptable; loadCatalog is fast and runs on mount. */
function buildBattleMeta(
  player: PlayerProfile,
  shop: NonNullable<ReturnType<typeof useSoloStore.getState>['session']>['shop'],
  meta: SoloBattleResponse['meta'],
  unitsById: Record<
    string,
    { name?: string; glyph?: string; is_flying?: boolean; body_offset_y?: number; body_scale?: number; element?: string; animal?: string }
  >,
): BattleMeta {
  // Brand-ribbon + kakejiku read most naturally as "team name vs team
  // name", with the human's display name as a smaller @tag. Solo
  // ghosts get a fitting opponent label.
  const ghostTeamName =
    meta.ghost_round !== null ? `Ghost ${meta.ghost_round}` : 'Procedural Ghost'
  const tagify = (s: string) => '@' + s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

  const unitGlyphs: Record<string, string> = {}
  const unitNames: Record<string, string> = {}
  const unitFlying: Record<string, boolean> = {}
  const unitBodyOffsetY: Record<string, number> = {}
  const unitBodyScale: Record<string, number> = {}
  const unitElements: Record<string, string> = {}
  const unitAnimals: Record<string, string> = {}
  for (const [id, def] of Object.entries(unitsById)) {
    if (def.glyph) unitGlyphs[id] = def.glyph
    if (def.name) unitNames[id] = def.name
    if (def.is_flying) unitFlying[id] = true
    if (def.body_offset_y) unitBodyOffsetY[id] = def.body_offset_y
    if (def.body_scale !== undefined && def.body_scale !== 1) unitBodyScale[id] = def.body_scale
    if (def.element) unitElements[id] = def.element
    if (def.animal) unitAnimals[id] = def.animal
  }
  return {
    round: shop.round - 1, // /ready returns next-round state; battle was for the previous round
    pairing: 1,
    seed: 'live',
    playerA: {
      name: player.team_name,
      tag: tagify(player.display_name),
      maxHp: shop.maxHealth,
      hp: shop.health,
      record: `${shop.wins}-${Math.max(0, shop.round - 1 - shop.wins)}`,
      mascot: player.icon as BattleMeta['playerA']['mascot'],
    },
    playerB: {
      name: ghostTeamName,
      tag: '@ghost',
      maxHp: 30,
      hp: 30,
      record: '—',
      mascot: 'tiger' as BattleMeta['playerB']['mascot'],
    },
    unitGlyphs,
    unitNames,
    unitFlying,
    unitBodyOffsetY,
    unitBodyScale,
    unitElements,
    unitAnimals,
    // Solo doesn't have a multi-style intermission — the player closes
    // the replay manually and lands back in the shop.
    nextRoundAtMs: null,
    // Player is always side A in solo (vs. a ghost on side B).
    viewerSide: 'A',
  }
}

/** End-of-run cap. Triggered when match.status flips to 'finished'
 *  on a win (10 wins) or loss (0 HP).
 *
 *  Both actions retire the current player identity — runs are
 *  single-use so ghost teams later harvested from past runs carry
 *  distinct display + team names. "Title Screen" returns to the main
 *  landing; "New Run" routes back to PlayerCreationScreen for a
 *  fresh profile. The server clears the cookie via /logout in both
 *  paths. */
function RunComplete({
  player,
  session,
  onSessionLost,
}: {
  player: PlayerProfile
  session: NonNullable<ReturnType<typeof useSoloStore.getState>['session']>
  onSessionLost: () => void
}): ReactElement {
  const logout = useSoloStore((s) => s.logout)
  const won = session.shop.wins >= session.shop.maxWins
  const Glyph = GLYPHS[player.icon as GlyphKey] ?? GLYPHS.crane!

  async function newRun() {
    await logout()
    // Drop into the player-creation flow. SoloRoute owns that fork
    // off /api/player/me — telling it the session is gone routes the
    // user to PlayerCreationScreen.
    onSessionLost()
  }
  async function returnToTitle() {
    await logout()
    window.location.search = ''
  }

  return (
    <div className="paper sps-page sps-page--finished">
      <div className="sps-final">
        <div className={`sps-final__hanko ${won ? 'sps-final__hanko--won' : 'sps-final__hanko--lost'}`}>
          <Glyph size={88} />
        </div>
        <h1 className="cute-title sps-final__title">
          {won ? 'A Triumphant Fold' : 'The Scroll Tears'}
        </h1>
        <p className="hand-note sps-final__subtitle">
          {won
            ? `${player.display_name} clinched ${session.shop.maxWins} wins.`
            : `${player.display_name} fell after round ${session.shop.round - 1}.`}
        </p>
        <div className="sps-final__actions">
          <button className="cute-btn sps-final__again" onClick={() => void newRun()}>
            New Run
          </button>
          <button
            className="cute-btn sps-final__title-btn"
            onClick={() => void returnToTitle()}
          >
            Title Screen
          </button>
        </div>
      </div>
    </div>
  )
}
